<template>
    <div class="wrapper wrapper--no-max-width">
        <header class="row__header">
            <h1>{{ $t(`profile.kyc.level4Text`) }}</h1>
        </header>

        <div :class="['kyc-form', { 'kyc-form--disabled' : !isFormEnabled }]">
            <div class="kyc-form__title">{{ $t('kycUserIdentity.confirmation') }}</div>
            <div class="kyc-form__content">
                <p>
                    {{ $t('kycLevel4IncomeDeclaration.paragraph1.begin') }}
                    <strong>{{ $t('kycLevel4IncomeDeclaration.paragraph1.name', {lastName, firstName}) }}</strong>
                    {{ $t('kycLevel4IncomeDeclaration.paragraph1.residing') }}
                    <strong>{{ $t('kycLevel4IncomeDeclaration.paragraph1.address', {address: userAddressToString}) }}</strong>
                    {{ $t('kycLevel4IncomeDeclaration.paragraph1.end') }}
                </p>

                <p>
                    {{ $t('kycLevel4DocumentCategoryDetails.intro.text') }}
                    <strong>{{ $t('kycLevel4DocumentCategoryDetails.intro.email', { userEmail: email }) }}</strong>.
                    {{ $t('kycLevel4DocumentCategoryDetails.end') }}
                    <a :href="`mailto:${$t('kycLevel4DocumentCategoryDetails.verificationEmailAddress')}`"
                        target="_blank"
                    >
                        {{ $t('kycLevel4DocumentCategoryDetails.verificationEmailAddress') }}
                    </a>
                </p>

                <p>{{ $t('kycLevel4IncomeDeclaration.paragraph2') }}</p>

                <ul class="categories">
                    <li v-for="(category, index) in categories"
                        :key="index"
                        :class="['category', { 'category--expanded': category.isExpanded }]"
                        @click="invertProperty(category, 'isExpanded')"
                    >
                        <input-checkbox :index="index"
                            :text="getTranslatedStr(category, 'name', category.name)"
                            :value="category.isSelected"
                            :maxWidth="false"
                            noBackground
                            isRound
                            isLabelCentered
                            isWholeAreaClickable
                            class="category__checkbox"
                            @toggleCheckBox="invertProperty(category, 'isSelected')"
                        />

                        <div class="category__chevron" />

                        <div class="category__details">
                            <ul v-if="getDocumentsRequired(category).length"
                                class="category__details-list"
                            >
                                <li class="category__details-item category__details-item--heading">
                                    {{ $t('kycLevel4DocumentCategoryDetails.documentsHeading') }}
                                </li>

                                <li v-for="(document, index) of getDocumentsRequired(category)"
                                    :key="index"
                                    class="category__details-item"
                                >
                                    {{ getTranslatedStr(document, 'name', document.name) }}
                                </li>
                            </ul>

                            <ul v-if="getInformationRequired(category).length"
                                class="category__details-list"
                            >
                                <li class="category__details-item category__details-item--heading">
                                    {{ $t('kycLevel4DocumentCategoryDetails.informationHeading') }}
                                </li>

                                <li v-for="(information, index) of getInformationRequired(category)"
                                    :key="index"
                                    class="category__details-item"
                                >
                                    {{ getTranslatedStr(information, 'name', information.name) }}
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>

                <p>{{ $t('kycLevel4IncomeDeclaration.paragraph3') }}</p>
                <p>{{ $t('kycLevel4IncomeDeclaration.paragraph4') }}</p>
                <p>{{ $t('kycLevel4IncomeDeclaration.paragraph5') }}</p>
                <p>{{ $t('kycLevel4IncomeDeclaration.paragraph6') }}</p>

                <button class="kyc-form__button"
                    :disabled="!isButtonEnabled"
                    @click="sendDeclaration()"
                >
                    {{ $t('global.button.confirm') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';

// Helpers
import globalMixin from '@shared/mixins/global-mixin.js';
import postalAddressMixin from '@webapp/helpers/postal-address-mixin.js';

// Components
import inputCheckbox from '@webapp/components/inputs/input-checkbox.vue';

export default {
    name: 'kyc-level-four',
    components: {
        inputCheckbox,
    },
    mixins: [
        globalMixin,
        postalAddressMixin,
    ],
    data() {
        return {
            categories: [],
        };
    },
    computed: {
        ...mapGetters([
            'kycDocumentCategories',
            'kycTierLevels',
            'userDetails',
            'getLatestValidationError',
            'hasUpdatedKycLevelVerification',
            'isPartnerApp',
            'getTranslatedStr',
        ]),

        firstName() {
            return this.userDetails.firstName;
        },

        lastName() {
            return this.userDetails.lastName;
        },

        email() {
            return this.userDetails.email;
        },

        /**
         * Boolean that indicates whether at least one category is selected
         *
         * @returns {boolean} A boolean
         */
        isButtonEnabled() {
            return this.categories.find((category) => category.isSelected);
        },

        isFormEnabled() {
            const level = this.kycTierLevels.find((item) => item.tierLevel === 4);

            if (level && (level.status === 'processing' || level.status === 'validated')) {
                return false;
            }

            return true;
        },
    },
    watch: {
        hasUpdatedKycLevelVerification: 'onKycValidationRequestCreation',

        kycDocumentCategories() {
            if (this.kycDocumentCategories.length) {
                // Enhance the category to contain some properties we need for selection and expanding / hiding details
                this.categories = this.kycDocumentCategories.map((category) => ({
                    ...category,
                    isExpanded: false,
                    isSelected: false,
                }));
            }

            this.showAppLoader({
                isVisible: false,
            });
        },
    },
    async created() {
        this.showAppLoader({
            isVisible: true,
            hasOpacity: true,
        });

        await this.getKycDocumentCategories({
            tierLevel: 4,
        });

        await this.getUserDetails();
    },
    methods: {
        ...mapActions({
            getUserDetails: sharedEpicTypes.GET_USER_DETAILS,
            getKycDocumentCategories: sharedEpicTypes.GET_KYC_DOCUMENTS_CATEGORIES,
            createValidationRequest: sharedEpicTypes.ADD_KYC_LEVEL_VERIFICATION,
        }),

        /**
         * On api validation
         *
         * @param {object} error The object received from api
         * @param {string} error.documents Documents error info
         * @returns {void}
         */
        onValidationError({ documents = '' }) {
            if (documents) {
                this.$notify({
                    type: 'error',
                    text: documents,
                });
            }

            this.showAppLoader({
                isVisible: false,
            });
        },

        /**
         * Creating the validation request
         *
         * @returns {void}
         */
        sendDeclaration() {
            if (!this.isButtonEnabled) {
                return;
            }

            if (!this.categories.length) {
                this.$notify({
                    type: 'error',
                    text: this.$t('kycLevel4IncomeDeclaration.errorEmptyCategories'),
                });

                return;
            }

            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            const payload = {
                tierLevel: 4,
                kycDocumentCategories: [],
            };

            this.categories.forEach((category, index) => {
                if (category.isSelected) {
                    payload.kycDocumentCategories.push(this.kycDocumentCategories[index]);
                }
            });

            this.createValidationRequest(payload);
        },

        /**
         * On Creation completed event
         *
         * @param {boolean} payload The payload sent back by the backend which is store in vuex
         * @returns {void}
         */
        onKycValidationRequestCreation(payload) {
            if (payload) {
                this.$notify({
                    type: 'success',
                    text: this.$t('kycLevel4IncomeDeclaration.onRequestCreatedMessage'),
                });

                this.showAppLoader({
                    isVisible: false,
                });
            }
        },

        /**
         * Invert the value of a boolean property
         *
         * @param {object} category Category to modify
         * @param {string} property Property to modify
         * @returns {void}
         */
        invertProperty(category, property) {
            category[property] = !category[property];
        },

        /**
         * Filter a list of documentTypes based on non-text format
         *
         * @param {object} payload Payload to pass to the function
         * @param {Array} payload.documentTypes List of documentTypes
         * @returns {Array} Filtered documentTypes not with text format
         */
        getDocumentsRequired({ documentTypes = [] }) {
            return documentTypes.filter((document) => document.format !== 'text');
        },

        /**
         * Filter a list of documentTypes based on text format
         *
         * @param {object} payload Payload to pass to the function
         * @param {Array} payload.documentTypes List of documentTypes
         * @returns {Array} Filtered documentTypes with text format
         */
        getInformationRequired({ documentTypes = [] }) {
            return documentTypes.filter((document) => document.format === 'text');
        },
    },
};
</script>

<style lang="scss" scoped>
.kyc-form {
    padding: 5.4rem 2rem;
    color: $cyprus-color;

    &--disabled {
        pointer-events: none;
        opacity: 0.5;
    }

    &__title {
        font-size: 1.7rem;
        line-height: 2.7rem;
        font-weight: 700;
        padding-bottom: 1rem;

        @include min-width(lg) {
            font-size: 2rem;
        }
    }

    &__content {
        font-size: 1.5rem;
        background: $white-color;
        padding: 3.3rem 1.9rem 3rem 2.5rem;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;

        @include min-width(md) {
            padding: 3.3rem 3.9rem 3rem 4.5rem;
        }

        a {
            color: $lima-color;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }

        .categories {
            margin: 4rem 0;
            padding: 0;
            list-style: none;

            .category {
                border-top: 0.2rem solid rgba($silver-color, 0.5);
                padding: 1rem 0 0.5rem;
                display: flex;
                flex-wrap: wrap;
                align-items: center;

                &:hover {
                    cursor: pointer;
                    background: $hawkes-color;
                }

                &:last-of-type {
                    border-bottom: 0.2rem solid rgba($silver-color, 0.5);
                }

                &--expanded {
                    .category {
                        &__details {
                            display: flex;
                        }

                        &__chevron {
                            transform: unset;
                            justify-content: flex-end;
                            padding: 0.5rem 1.5rem 1.5rem;
                        }
                    }
                }

                &__checkbox {
                    max-width: 80%;
                }

                &__chevron {
                    padding: 1.5rem 1.5rem 0.5rem;
                    display: flex;
                    flex: 1;
                    transform: rotate(180deg);
                    justify-content: flex-start;
                    @include chevron($cyprus-color, 3.25rem, true);

                    &:hover {
                        cursor: pointer;
                    }
                }

                &__details {
                    width: 100%;
                    display: none;
                    flex-direction: column;
                    padding: 1rem 2rem 2rem 5.5rem;

                    @include min-width(lg) {
                        flex-direction: row;
                    }

                    &-list {
                        padding: 0;
                        list-style: none;

                        @include min-width(lg) {
                            width: 50%;
                        }

                        &:last-of-type {
                            margin-top: 2rem;

                            @include min-width(lg) {
                                width: 50%;
                                margin-left: 3rem;
                                margin-top: unset;
                            }
                        }
                    }

                    &-item {
                        padding: 0.5rem 0 1rem 2.5rem;
                        position: relative;

                        &:before {
                            color: $lima-color;
                            font-family: "icomoon";
                            content: "\e919";
                            border: 0.1rem solid;
                            border-radius: 100%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 0.75rem;
                            width: 1.5rem;
                            height: 1.5rem;
                            position: absolute;
                            left: 0;
                            top: 0.6rem;
                        }

                        &--heading {
                            font-weight: bold;
                            padding-left: unset;

                            &:before {
                                content: unset;
                            }
                        }
                    }
                }
            }
        }
    }

    &__button {
        margin-top: 1.4rem;
        height: 4rem;
        width: 16rem;
        border-radius: 0.5rem;
        background: $lima-color;
        text-align: center;
        font-size: 1.4rem;
        line-height: 4rem;
        color: $daintree-color;
        border: none;
        align-self: flex-end;

        &:hover {
            cursor: pointer;
            opacity: 0.8;
        }

        &:disabled {
            background: $lima-color-opaque;
            cursor: default;
        }

        @include min-width(lg) {
            width: 22rem;
        }
    }
}
</style>